"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  CONSENT_EVENT,
  getConsent,
  loadPixel,
  setConsent,
  trackPageView,
  type Consent,
} from "../../lib/meta-pixel";

/**
 * Loads the Meta Pixel only after the shopper accepts, fires PageView on the
 * first load and on every client-side navigation, and shows the consent bar
 * until a choice is made. "Cookie choices" in the footer clears the choice,
 * which brings the bar back.
 */
export default function MetaPixel() {
  const pathname = usePathname();
  const [consent, setLocal] = useState<Consent | null | "unknown">("unknown");
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const sync = () => setLocal(getConsent());
    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CONSENT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    if (consent !== "granted") return;
    loadPixel();
    if (lastPath.current !== pathname) {
      lastPath.current = pathname;
      trackPageView();
    }
  }, [consent, pathname]);

  if (consent !== null) return null;

  return (
    <div
      role="region"
      aria-label="Cookie choices"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-paper/15 bg-ink text-paper"
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-4 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-prose text-[13px] leading-relaxed text-paper/85">
          We use a Meta (Facebook and Instagram) cookie to measure our ads and show you our pieces
          there. It only loads if you accept.{" "}
          <Link href="/privacy" className="underline hover:text-gold">
            Privacy Policy
          </Link>
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => setConsent("denied")}
            className="border border-paper/40 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] transition hover:border-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => setConsent("granted")}
            className="bg-paper px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-ink transition hover:bg-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

/** Footer link that clears the stored choice so the consent bar shows again. */
export function CookieChoicesButton({ className }: { className?: string }) {
  return (
    <button type="button" onClick={() => setConsent(null)} className={className}>
      Cookie choices
    </button>
  );
}
